/**
 * Canonical saved door-design intent: product selections only.
 * Calculated layout and SVG rendering belong elsewhere.
 */

import { DEFAULT_EXTERNAL_SLAB_STYLE_ID } from '../assets/externalSlabCatalogue';
import { DEFAULT_GLASS_DESIGN_ID } from '../assets/glassCatalogue';
import { DEFAULT_THRESHOLD_COLOUR_ID } from '../colour/productColours';
import {
  DEFAULT_HARDWARE_COLOUR_FAMILY_ID,
  type HardwareColourFamilyId,
} from '../hardware/hardwareCatalog';

/** Overall installation size in millimetres. */
export interface DoorDimensions {
  overallWidthMm: number;
  overallHeightMm: number;
}

/** Authored SVG artwork families. */
export type FrameArtworkProfile = 'chamfered' | 'sculptured' | 'timberbutt';
/** Product-facing frame choices. Butt Joint uses the timberbutt artwork. */
export type FrameProfile = 'chamfered' | 'sculptured' | 'butt-joint';

export interface DoorAncillaries {
  /** Optional projecting cill below the threshold. */
  hasCill: boolean;
  /**
   * Normal mandatory lower-door component. Enabled by default.
   * A missing threshold remains supported and uses the same contiguous-contact rule.
   */
  hasThreshold: boolean;
}

export type DoorHanding = 'left' | 'right';

/** Solid infill used when no catalogue style is selected or an asset cannot load. */
export const PLACEHOLDER_SOLID_SLAB_STYLE_ID = 'placeholder-solid';

export interface DoorSlabConfiguration {
  styleId: string;
  /** External-face finish-library id (or a hex). Independent of frame and cill colour. */
  colourId: string;
  /** Internal-face finish. May differ from `colourId`. */
  internalColourId: string;
  handing: DoorHanding;
}

export interface DoorGlazingSelection {
  id: string;
}

export interface DoorHardwareSelection {
  id: string;
  /** Optional SVG placement id, for example `letterplate-position-1`. */
  placementId?: string;
}

/**
 * Face-specific hardware. External and internal selections are independent and
 * must not be assumed to mirror each other. Both may still share a physical
 * lock position from `#hardware-guides` when the configuration allows it.
 */
export interface DoorHardwareConfiguration {
  /** Furniture metal finish. Filters range products; default Chrome. */
  colourFamilyId: HardwareColourFamilyId;
  external: DoorHardwareSelection[];
  internal: DoorHardwareSelection[];
}

export interface DoorConfiguration {
  dimensions: DoorDimensions;
  frameProfile: FrameProfile;
  /** External-face finish-library id (or a hex) for the frame. Independent of slab and cill colour. */
  frameColourId: string;
  /** Internal-face frame finish. May differ from `frameColourId`. */
  internalFrameColourId: string;
  /** External-face finish-library id (or a hex) for the cill. Independent of frame and slab colour. */
  cillColourId: string;
  /** Internal-face cill finish. May differ from `cillColourId`. */
  internalCillColourId: string;
  /** External-face finish-library id (or a hex) for the threshold. Independent of frame and cill. */
  thresholdColourId: string;
  /** Internal-face threshold finish. May differ from `thresholdColourId`. */
  internalThresholdColourId: string;
  ancillaries: DoorAncillaries;
  slab: DoorSlabConfiguration;
  glazing: DoorGlazingSelection[];
  hardware: DoorHardwareConfiguration;
}

/** Development helper. Threshold and cill are enabled by default. */
export function createDefaultDoorConfiguration(): DoorConfiguration {
  return {
    dimensions: {
      overallWidthMm: 900,
      overallHeightMm: 2100,
    },
    frameProfile: 'chamfered',
    frameColourId: 'white',
    internalFrameColourId: 'white',
    cillColourId: 'white',
    internalCillColourId: 'white',
    thresholdColourId: DEFAULT_THRESHOLD_COLOUR_ID,
    internalThresholdColourId: DEFAULT_THRESHOLD_COLOUR_ID,
    ancillaries: {
      hasCill: true,
      hasThreshold: true,
    },
    slab: {
      styleId: DEFAULT_EXTERNAL_SLAB_STYLE_ID,
      colourId: 'white',
      internalColourId: 'white',
      handing: 'left',
    },
    glazing: [{ id: DEFAULT_GLASS_DESIGN_ID }],
    hardware: {
      colourFamilyId: DEFAULT_HARDWARE_COLOUR_FAMILY_ID,
      external: [],
      internal: [],
    },
  };
}
